/**
 * WhatsApp Config Page — Admin UI for WhatsApp bot settings
 *
 * Configure:
 * - Bot connection status and phone number
 * - Operator groups and auto-reply settings
 * - Customer bot enable/disable
 * - Notification preferences (arrival alerts, digest, AR reminders)
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Phone, Users, Bell, Bot, Shield, Loader2, Check, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';

interface WhatsAppConfig {
  connected: boolean;
  phoneNumber: string;
  operatorGroupId: string;
  customerBotEnabled: boolean;
  autoReply: boolean;
  language: 'en' | 'zh' | 'auto';
  notifications: {
    arrivalAlerts: boolean;
    dailyDigest: boolean;
    arReminders: boolean;
    jobCreated: boolean;
    approvalRequests: boolean;
  };
}

const DEFAULT_CONFIG: WhatsAppConfig = {
  connected: false,
  phoneNumber: '',
  operatorGroupId: '',
  customerBotEnabled: false,
  autoReply: true,
  language: 'auto',
  notifications: {
    arrivalAlerts: true,
    dailyDigest: true,
    arReminders: true,
    jobCreated: true,
    approvalRequests: true,
  },
};

export default function WhatsAppConfigPage() {
  const [config, setConfig] = useState<WhatsAppConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get<{ data: WhatsAppConfig }>('/admin/whatsapp-config')
      .then((res) => setConfig({ ...DEFAULT_CONFIG, ...(res.data ?? {}) }))
      .catch(() => { /* use defaults */ })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/admin/whatsapp-config', config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  }, [config]);

  const updateNotification = (key: keyof WhatsAppConfig['notifications'], value: boolean) => {
    setConfig(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">WhatsApp Configuration</h2>
          <p className="text-sm text-muted-foreground">Configure bot connection and notification settings</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : saved ? <Check className="h-4 w-4 mr-2" /> : null}
          {saved ? 'Saved' : 'Save Changes'}
        </Button>
      </div>

      {/* Connection Status */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            <CardTitle className="text-sm">Connection</CardTitle>
            <Badge variant={config.connected ? 'default' : 'secondary'} className={`text-[10px] ${config.connected ? 'bg-green-100 text-green-700' : ''}`}>
              {config.connected ? 'Connected' : 'Not Connected'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Bot Phone Number</label>
              <Input
                className="h-8 text-sm mt-1"
                placeholder="+65 XXXX XXXX"
                value={config.phoneNumber}
                onChange={(e) => setConfig(prev => ({ ...prev, phoneNumber: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Operator Group ID</label>
              <Input
                className="h-8 text-sm mt-1"
                placeholder="e.g. 120363424459792965@g.us"
                value={config.operatorGroupId}
                onChange={(e) => setConfig(prev => ({ ...prev, operatorGroupId: e.target.value }))}
              />
            </div>
          </div>
          {!config.connected && (
            <Button size="sm" variant="outline" className="h-7 text-xs">
              <RefreshCw className="h-3 w-3 mr-1" /> Reconnect
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Bot Settings */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            <CardTitle className="text-sm">Bot Settings</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Customer Bot</div>
              <div className="text-xs text-muted-foreground">Enable public-facing bot for BL/container inquiries</div>
            </div>
            <Switch
              checked={config.customerBotEnabled}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, customerBotEnabled: v }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Auto-Reply</div>
              <div className="text-xs text-muted-foreground">Automatically reply to unrecognized commands with help text</div>
            </div>
            <Switch
              checked={config.autoReply}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, autoReply: v }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Language</div>
              <div className="text-xs text-muted-foreground">Bot response language</div>
            </div>
            <Select value={config.language} onValueChange={(v) => setConfig(prev => ({ ...prev, language: v as 'en'|'zh'|'auto' }))}>
              <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="zh">中文</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <CardTitle className="text-sm">Notifications</CardTitle>
          </div>
          <CardDescription className="text-xs">Push notifications sent to the operator WhatsApp group</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {([
            ['arrivalAlerts', 'Arrival Alerts', 'Notify when vessels arrive at POD'],
            ['dailyDigest', 'Daily Digest', 'Morning KPI summary at 8:00 AM'],
            ['arReminders', 'AR Reminders', 'Overdue AR collection reminders'],
            ['jobCreated', 'Job Created', 'Notify when new jobs are created from email'],
            ['approvalRequests', 'Approval Requests', 'Send approval requests for low-margin jobs'],
          ] as const).map(([key, label, desc]) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <div className="text-sm">{label}</div>
                <div className="text-xs text-muted-foreground">{desc}</div>
              </div>
              <Switch
                checked={config.notifications[key]}
                onCheckedChange={(v) => updateNotification(key, v)}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Command Reference */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <CardTitle className="text-sm">Command Reference</CardTitle>
          </div>
          <CardDescription className="text-xs">Available commands for operators in WhatsApp</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              ['#job SI-001', 'Lookup specific job'],
              ['today arrivals', 'Ships arriving today'],
              ['week arrivals', 'Ships arriving this week'],
              ['ar overdue', 'Overdue AR summary'],
              ['dashboard', 'KPI overview'],
              ['recent jobs', 'Last 10 jobs'],
              ['help', 'Show all commands'],
            ].map(([cmd, desc]) => (
              <div key={cmd} className="flex items-start gap-2 p-2 rounded bg-muted/50">
                <code className="text-[10px] bg-background px-1 py-0.5 rounded font-mono shrink-0">{cmd}</code>
                <span className="text-muted-foreground">{desc}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
